import React, { useContext, useMemo } from 'react';
import { Alert, Linking, ScrollView, StyleSheet, TouchableOpacity, View } from 'react-native';
import { Button, Switch, Text } from 'react-native-paper';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { useTranslation } from 'react-i18next';
import { useRouter } from 'expo-router';

import JobsContext from '../context/JobsContext';
import { Palette, radius, spacing, useThemePalette } from '../theme';
import { SUPPORTED_LANGUAGES, getDeviceLanguage } from '../i18n';
import { track } from '../lib/analytics';
import useAuthUser from '../lib/auth/useAuthUser';
import ModalSheet from './ModalSheet';

interface SettingsModalProps {
  visible: boolean;
  onDismiss: () => void;
}

// Appearance/language/tour preferences. Account, sign-in and Remove Ads live
// on the Profile tab now.
const SettingsModal: React.FC<SettingsModalProps> = ({ visible, onDismiss }) => {
  const { t, i18n } = useTranslation();
  const router = useRouter();
  const palette = useThemePalette();
  const styles = useMemo(() => makeStyles(palette), [palette]);
  const { themeMode, setThemeMode, language, setLanguage, hideAds, setHideAds, deleteAccount } =
    useContext(JobsContext);
  const user = useAuthUser();

  const activeLanguage = language || i18n.language || getDeviceLanguage();

  const handleThemeChange = (mode: 'light' | 'dark') => {
    if (mode === themeMode) return;
    setThemeMode(mode);
    track('theme_toggle', { mode });
  };

  const handleLanguageChange = (code: string) => {
    if (code === activeLanguage) return;
    setLanguage(code);
    track('language_change', { language: code });
  };

  const handleHideAdsChange = (value: boolean) => {
    setHideAds(value);
    track('dev_hide_ads_toggle', { hidden: value });
  };

  const handleReplayTour = () => {
    track('tour_replay');
    onDismiss();
    router.push({ pathname: '/onboarding', params: { replay: '1' } });
  };

  const handleOpenPrivacy = () => {
    onDismiss();
    router.push('/privacy_policy');
  };

  const handleOpenWebsite = () => {
    Linking.openURL('https://findremoteworks.com').catch(() => undefined);
  };

  const handleDeleteAccount = () => {
    Alert.alert(
      t('settings.deleteAccount.confirmTitle'),
      t('settings.deleteAccount.confirmMessage'),
      [
        { text: t('common.cancel'), style: 'cancel' },
        {
          text: t('settings.deleteAccount.action'),
          style: 'destructive',
          onPress: async () => {
            track('delete_account');
            const result = await deleteAccount();
            if (result.ok) {
              onDismiss();
              return;
            }
            Alert.alert(
              t('settings.deleteAccount.errorTitle'),
              result.reason === 'requires-recent-login'
                ? t('settings.deleteAccount.reauthMessage')
                : t('settings.deleteAccount.errorMessage')
            );
          },
        },
      ]
    );
  };

  return (
    <ModalSheet visible={visible} onDismiss={onDismiss} cardStyle={styles.card}>
      <View style={styles.body}>
        <View style={styles.header}>
          <Text style={styles.title}>{t('settings.title')}</Text>
        </View>

        <ScrollView style={styles.scroll} contentContainerStyle={styles.scrollContent}>
          <Text style={styles.sectionTitle}>{t('settings.theme')}</Text>
          <View style={styles.themeRow}>
            {(['light', 'dark'] as const).map((mode) => {
              const selected = themeMode === mode;
              return (
                <TouchableOpacity
                  key={mode}
                  onPress={() => handleThemeChange(mode)}
                  style={[styles.themeChip, selected && styles.themeChipSelected]}
                  activeOpacity={0.85}
                >
                  <MaterialCommunityIcons
                    name={mode === 'dark' ? 'weather-night' : 'weather-sunny'}
                    size={18}
                    color={selected ? palette.appliedText : palette.textPrimary}
                  />
                  <Text style={[styles.themeChipText, selected && styles.themeChipTextSelected]}>
                    {t(mode === 'dark' ? 'settings.themeDark' : 'settings.themeLight')}
                  </Text>
                </TouchableOpacity>
              );
            })}
          </View>

          <Text style={[styles.sectionTitle, { marginTop: spacing.lg }]}>
            {t('settings.language')}
          </Text>
          <View style={styles.languageList}>
            {SUPPORTED_LANGUAGES.map((lang) => {
              const selected = activeLanguage === lang.code;
              return (
                <TouchableOpacity
                  key={lang.code}
                  onPress={() => handleLanguageChange(lang.code)}
                  style={[styles.languageRow, selected && styles.languageRowSelected]}
                  activeOpacity={0.85}
                >
                  <Text style={[styles.languageLabel, selected && styles.languageLabelSelected]}>
                    {lang.label}
                  </Text>
                  {selected && (
                    <MaterialCommunityIcons name="check" size={18} color={palette.appliedText} />
                  )}
                </TouchableOpacity>
              );
            })}
          </View>

          <Text style={[styles.sectionTitle, { marginTop: spacing.lg }]}>{t('settings.tour')}</Text>
          <TouchableOpacity onPress={handleReplayTour} style={styles.languageRow} activeOpacity={0.85}>
            <Text style={styles.languageLabel}>{t('settings.replayTour')}</Text>
            <MaterialCommunityIcons
              name="play-circle-outline"
              size={20}
              color={palette.textPrimary}
            />
          </TouchableOpacity>

          <Text style={[styles.sectionTitle, { marginTop: spacing.lg }]}>{t('settings.about')}</Text>
          <TouchableOpacity onPress={handleOpenPrivacy} style={styles.languageRow} activeOpacity={0.85}>
            <Text style={styles.languageLabel}>{t('settings.privacyPolicy')}</Text>
            <MaterialCommunityIcons name="shield-account-outline" size={20} color={palette.textPrimary} />
          </TouchableOpacity>
          <TouchableOpacity onPress={handleOpenWebsite} style={styles.languageRow} activeOpacity={0.85}>
            <Text style={styles.languageLabel}>{t('settings.website')}</Text>
            <MaterialCommunityIcons name="open-in-new" size={20} color={palette.textPrimary} />
          </TouchableOpacity>

          {__DEV__ && (
            <>
              <Text style={[styles.sectionTitle, { marginTop: spacing.lg }]}>
                {t('settings.developer')}
              </Text>
              <View style={styles.toggleRow}>
                <Text style={styles.toggleLabel}>{t('settings.hideAds')}</Text>
                <Switch value={hideAds} onValueChange={handleHideAdsChange} color={palette.primary} />
              </View>
            </>
          )}

          {user && (
            <>
              <Text style={[styles.sectionTitle, { marginTop: spacing.lg }]}>
                {t('settings.account')}
              </Text>
              <TouchableOpacity
                onPress={handleDeleteAccount}
                style={styles.dangerRow}
                activeOpacity={0.85}
              >
                <MaterialCommunityIcons name="trash-can-outline" size={20} color={palette.danger} />
                <Text style={styles.dangerText}>{t('settings.deleteAccount.action')}</Text>
              </TouchableOpacity>
            </>
          )}
        </ScrollView>

        <View style={styles.footer}>
          <Button
            mode="contained"
            onPress={onDismiss}
            buttonColor={palette.elevated}
            textColor={palette.textOnDark}
            style={styles.footerBtn}
          >
            {t('settings.close')}
          </Button>
        </View>
      </View>
    </ModalSheet>
  );
};

const makeStyles = (p: Palette) =>
  StyleSheet.create({
    card: { backgroundColor: p.surface, borderRadius: radius.lg },
    body: { padding: spacing.lg },
    header: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: spacing.md,
    },
    title: { fontSize: 20, fontWeight: '700', color: p.textPrimary },
    scroll: { maxHeight: 480 },
    scrollContent: { paddingBottom: spacing.md },
    sectionTitle: {
      fontSize: 15,
      fontWeight: '700',
      color: p.textPrimary,
      marginBottom: spacing.sm,
    },
    dangerRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: spacing.sm,
      paddingVertical: spacing.md,
      paddingHorizontal: spacing.md,
      borderRadius: radius.md,
      backgroundColor: p.surfaceMuted,
      borderWidth: 1,
      borderColor: p.border,
    },
    dangerText: { fontSize: 15, fontWeight: '600', color: p.danger },
    themeRow: { flexDirection: 'row', gap: spacing.sm },
    themeChip: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: spacing.xs,
      paddingHorizontal: spacing.md,
      paddingVertical: spacing.sm,
      borderRadius: radius.pill,
      backgroundColor: p.surfaceMuted,
      borderWidth: 1,
      borderColor: p.border,
    },
    themeChipSelected: { backgroundColor: p.appliedBg, borderColor: p.appliedBg },
    themeChipText: { color: p.textPrimary, fontSize: 13, fontWeight: '600' },
    themeChipTextSelected: { color: p.appliedText },
    languageList: { gap: spacing.xs },
    languageRow: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingHorizontal: spacing.md,
      paddingVertical: spacing.md,
      borderRadius: radius.md,
      backgroundColor: p.surfaceMuted,
      borderWidth: 1,
      borderColor: p.border,
    },
    languageRowSelected: { backgroundColor: p.appliedBg, borderColor: p.appliedBg },
    languageLabel: { color: p.textPrimary, fontSize: 14, fontWeight: '500' },
    languageLabelSelected: { color: p.appliedText, fontWeight: '700' },
    toggleRow: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingHorizontal: spacing.md,
      paddingVertical: spacing.sm,
      borderRadius: radius.md,
      backgroundColor: p.surfaceMuted,
      borderWidth: 1,
      borderColor: p.border,
    },
    toggleLabel: { color: p.textPrimary, fontSize: 14, fontWeight: '500' },
    footer: {
      flexDirection: 'row',
      justifyContent: 'flex-end',
      gap: spacing.sm,
      marginTop: spacing.md,
    },
    footerBtn: { borderRadius: radius.pill },
  });

export default SettingsModal;
